import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { getAccessToken } from '../services/api';
import { getApiUrl } from '../config/runtimeConfig';

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

const money = value => {
  const n = Number(value || 0);
  return n > 0 ? `₦${n.toLocaleString()}` : 'Free';
};

export default function WhotRoomInvitationListener() {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!getAccessToken()) return;
    try {
      const res = await api.rooms.getMyInvitations();
      setInvitations(res.data.invitations || []);
      setError('');
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!getAccessToken()) return undefined;
    load();
    const socket = io(SOCKET_URL, {
      auth: { token: getAccessToken() },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socket.on('room:invitation', invitation => {
      setInvitations(prev => [invitation, ...prev.filter(i => i.invitationId !== invitation.invitationId)]);
    });
    socket.on('room:invitationCancelled', data => {
      setInvitations(prev => prev.filter(i => i.invitationId !== data.invitationId));
    });
    const timer = setInterval(load, 15000);
    return () => { clearInterval(timer); socket.disconnect(); };
  }, [load]);

  const act = async (invitationId, action) => {
    setBusy(invitationId);
    setError('');
    try {
      if (action === 'accept') {
        const res = await api.rooms.acceptInvitation(invitationId);
        setInvitations(prev => prev.filter(i => i.invitationId !== invitationId));
        navigate(`/room-waiting/${res.data.roomId}`);
      } else {
        await api.rooms.declineInvitation(invitationId);
        setInvitations(prev => prev.filter(i => i.invitationId !== invitationId));
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Unable to process invitation';
      setError(message);
      await load();
    } finally {
      setBusy('');
    }
  };

  if (!invitations.length) return null;
  const invitation = invitations[0];

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Whot room invitation">
      <div style={styles.card}>
        <div style={styles.icon}>🎴</div>
        <div style={styles.kicker}>WHOT ROOM INVITATION</div>
        <h2 style={styles.title}>{invitation.hostUsername || 'A friend'} invited you</h2>
        <p style={styles.message}>Join their Whot room?</p>
        <div style={styles.meta}>
          <span>🎴 WHOT</span>
          <span>{invitation.playerCount} players</span>
          <span>{money(invitation.entryFee)}</span>
        </div>
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.actions}>
          <button disabled={!!busy} onClick={() => act(invitation.invitationId, 'decline')} style={styles.decline}>Decline</button>
          <button disabled={!!busy} onClick={() => act(invitation.invitationId, 'accept')} style={styles.accept}>{busy ? 'Please wait…' : 'Accept'}</button>
        </div>
        {invitations.length > 1 && <div style={styles.more}>+{invitations.length - 1} more invitation(s)</div>}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(0,0,0,.65)', display: 'grid', placeItems: 'center', padding: 18 },
  card: { width: 'min(430px, 100%)', background: '#151522', color: '#fff', border: '1px solid #34344a', borderRadius: 20, padding: 24, boxShadow: '0 20px 70px rgba(0,0,0,.45)' },
  icon: { width: 58, height: 58, borderRadius: 16, display: 'grid', placeItems: 'center', background: 'rgba(255,145,0,.14)', fontSize: 30, marginBottom: 14 },
  kicker: { color: '#ff9800', fontSize: 11, fontWeight: 800, letterSpacing: 1.5 },
  title: { margin: '7px 0 5px', fontSize: 21 },
  message: { margin: 0, color: '#aaa' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '18px 0', color: '#ddd', fontSize: 13 },
  metaItem: {},
  error: { background: 'rgba(255,68,68,.12)', border: '1px solid rgba(255,68,68,.35)', color: '#ff7777', padding: 10, borderRadius: 9, fontSize: 13, marginBottom: 12 },
  actions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  decline: { padding: 13, borderRadius: 10, border: '1px solid #444', background: '#222', color: '#ddd', fontWeight: 700, cursor: 'pointer' },
  accept: { padding: 13, borderRadius: 10, border: 0, background: '#ff9800', color: '#111', fontWeight: 800, cursor: 'pointer' },
  more: { marginTop: 10, color: '#777', fontSize: 12, textAlign: 'center' },
};
